import {
    componentName,
    componentClasses,
    schema,
    defaultProps,
    eventMap,
    views,
} from "../../../core/components/modal/modal";
import { getFocusableElements, createElement } from "../../../core/js/utilities";
import {
    createStore,
    renderState,
    dispatchEvent,
    reducers,
    actions,
} from "../../../core/js/state-machine";
import { closeX } from "../../../core/js/helpers/built-in-icons";
import validateInitialization from "../../../core/js/helpers/validate-initialization";

/**
 * Creates Modal
 * @param {HTMLElement} node
 * @returns Modal
 */

const validateHTML = (element) => {
    const header = element.querySelector(`.${componentClasses.header}`);

    return header ? { header } : false;
};

export default function Modal(element, props) {
    const [success, mergedProps, validElements] = validateInitialization({
        componentName,
        element,
        defaultProps,
        props,
        schema,
        validateHTML,
    });
    if (!success) return;

    // Close button initialization build for the modal
    const buildCloseButton = () => {
        closeButton = createElement("button", {
            class: componentClasses.closeButton,
            html: closeX(),
        });
        closeButton.setAttribute("aria-label", mergedProps.srCloseLabel);
        closeButton.setAttribute("type", mergedProps.type);
        validElements.header.insertBefore(closeButton, validElements.header.children[0]);
        closeButton = element.querySelector(`.${componentClasses.closeButton}`);
    };

    //Validating the closeButton is existing, checking if passing props have trigger parameter to initialize trigger function for the element
    const setTriggerElements = () => {
        if (mergedProps.trigger) {
            //Defining the modal trigger if any trigger element set
            try {
                triggerElements = Array.from(document.querySelectorAll(mergedProps.trigger));
                triggerSelector = mergedProps.trigger;
                if (triggerElements.length === 0) {
                    console.warn(
                        `${componentName} No trigger elements have been found with id=${triggerSelector} attribute.`
                    );
                }
            } catch (error) {
                console.error(
                    `${componentName} failed to query for ${mergedProps.trigger}: ${error.message}`
                );
                return;
            }
        }
    };

    //Handling the state changes for the component
    const handleStateChange = (modalOpenState) => {
        renderState(modalOpenState ? views.opened : views.closed, element);
        modalOpenState
            ? dispatchEvent(eventMap.opened, element)
            : dispatchEvent(eventMap.closed, element);
        if (document.body.classList.contains("user-is-tabbing")) {
            modalOpenState
                ? setTimeout(() => {
                      return closeButton.focus();
                  }, 0)
                : setTimeout(() => {
                      return triggerSelector && document.querySelector(triggerSelector).focus();
                  }, 0);
        }
    };

    const dispose = () => {
        closeButton.removeEventListener("click", close);
        triggerElements.forEach((trigger) => {
            trigger.removeEventListener("click", open);
        });
        triggerElements = null;
        element[componentName] = null;
        element = null;
    };

    //Helper function to fire the dispatch action to close the modal
    const close = () => {
        modalStore.dispatch(actions.close());
    };

    //Helper function to fire the dispatch action to open the modal
    const open = () => {
        modalStore.dispatch(actions.open());
    };

    const handleTabbing = (event) => {
        let allFocusableElements;
        try {
            allFocusableElements = getFocusableElements(element);
        } catch (e) {
            console.error(e);
        }

        //If user wants to tab through the modal at any point focus the first element
        if (
            element.classList.contains(componentClasses.show) &&
            !document.body.classList.contains("user-is-tabbing")
        ) {
            setTimeout(() => {
                return allFocusableElements[0].focus();
            }, 0);
        }

        // If the last focusable element for the modal -> go to 1 one
        if (
            document.activeElement === allFocusableElements[allFocusableElements.length - 1] &&
            !event.shiftKey
        ) {
            setTimeout(() => {
                return allFocusableElements[0].focus();
            }, 0);
        }

        // If the first one element of the modal and tabbing with shift go to the last
        if (document.activeElement === allFocusableElements[0] && event.shiftKey) {
            setTimeout(() => {
                return allFocusableElements[allFocusableElements.length - 1].focus();
            }, 0);
        }
    };

    //Keyboard downbutton functions
    const handleKeydown = (event) => {
        switch (event.keyCode) {
            // Esc key
            case 27:
                close();
                break;
            case 9:
                handleTabbing(event);
                break;
        }
    };

    let closeButton;
    let triggerElements;
    let triggerSelector;
    //Initializing the store
    const modalStore = createStore(reducers.boolReducer, mergedProps.initialState);

    modalStore.subscribe(handleStateChange);

    //Creating the event listeners for the elements
    buildCloseButton();
    setTriggerElements();
    closeButton.addEventListener("click", close);
    element.addEventListener("keydown", handleKeydown);
    triggerElements.forEach((trigger) => {
        return trigger.addEventListener("click", open);
    });

    //Disposing the elements from the reducer
    const api = {
        element,
        dispose,
        close,
        open,
    };

    element[componentName] = api;
    return api;
}
